import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/utils";

export const MobileService = () => {
  const { t } = useLanguage();
  const m = t.mobile;
  return (
    <section id="mobile" className="relative py-28 sm:py-36 bg-[#0E0E0E] overflow-hidden" data-testid="mobile-section">
      {/* ambient gold glow */}
      <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-[#D4AF37]/10 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-52 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#D4AF37]/[0.07] blur-[130px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{m.label}</span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-[#F4EFE4]">
            {m.title}
          </h2>
          <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed max-w-xl">{m.sub}</p>

          <ul className="mt-9 space-y-4">
            {m.bullets.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="flex items-start gap-3.5"
                data-testid={`mobile-bullet-${i + 1}`}
              >
                <span className="mt-0.5 flex items-center justify-center w-6 h-6 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                </span>
                <span className="text-[#F4EFE4]/85 text-sm sm:text-base leading-relaxed">{b}</span>
              </motion.li>
            ))}
          </ul>

          <button
            onClick={() => scrollToId("#quote")}
            data-testid="mobile-cta"
            className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] text-[#111111] font-semibold text-sm sm:text-base px-7 py-3.5 hover:bg-[#e6c65a] transition-colors duration-300"
          >
            {m.cta}
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="space-y-5"
        >
          <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30 shadow-[0_0_60px_-15px_rgba(212,175,55,0.35)]">
            <video
              data-testid="mobile-van-video"
              className="w-full aspect-video object-cover"
              autoPlay
              muted
              loop
              playsInline
              poster="/assets/van-hero.jpg"
            >
              <source src="/assets/van-cinematic.webm" type="video/webm" />
              <source src="/assets/van-cinematic.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            <span className="absolute bottom-4 left-5 text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4AF37]">
              ClearRoute · Mobile Unit 01
            </span>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div className="group rounded-2xl overflow-hidden border border-white/10">
              <img
                src="/assets/tech-replace.jpg"
                alt="Technician replacing a windshield"
                loading="lazy"
                data-testid="mobile-img-replace"
                className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="group rounded-2xl overflow-hidden border border-white/10">
              <img
                src="/assets/mobile-scene.jpg"
                alt="ClearRoute mobile van on a driveway"
                loading="lazy"
                data-testid="mobile-img-scene"
                className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
